import React, { useState } from 'react';
import { FileSpreadsheet, FileText } from 'lucide-react';
import { toast } from 'react-toastify';
import { exportToXlsx, exportToPdf, exportReportPdf, exportReportXlsx } from '../utils/exporters';

/**
 * Reusable Excel + PDF export controls.
 *
 * Two modes:
 *
 * 1. Single table (original) — pass `columns` + `getRows` (+ optional `getSummary`).
 * 2. Full report (`getSections`) — pass a function returning an array of titled sections, so the
 *    export reproduces EVERYTHING on screen instead of one table. Each section is either
 *    `{ kind: 'kpi', title, items: [{label, value}] }` or
 *    `{ title, columns: [{key, label, numeric?, money?, total?, avg?}], rows, totals? }`.
 *    Numeric columns get a TOTAL by default (`total: false` opts out, for rates and shares).
 *    An AVERAGE line appears ONLY for columns marked `avg: true` — averaging everything is
 *    noise. Prefer this mode for report tabs.
 *
 * Props:
 *   filename    base file name (no extension)
 *   title       report title (sheet name + PDF heading)
 *   subtitle    optional PDF subheading (e.g. active date range / filters)
 *   columns     [{ key, label }]                       — mode 1
 *   getRows     () => rows | Promise<rows>              — mode 1. Fetch the FULL filtered dataset
 *               at click time, not just the rows currently in view.
 *   getSummary  (rows) => [{ label, value }]            — mode 1. MUST return an array.
 *   getSections () => sections | Promise<sections>      — mode 2, takes precedence over mode 1.
 *   orientation 'portrait' | 'landscape'
 *   size        button size class suffix ('sm' default)
 */
export default function ExportButtons({
  filename, title, subtitle, columns, getRows, getSummary, getSections, orientation, size = 'sm',
}) {
  const [busy, setBusy] = useState(false);

  const run = async (kind) => {
    if (busy) return;
    setBusy(true);
    try {
      if (getSections) {
        const sections = (await getSections())?.filter(Boolean) ?? [];
        const hasData = sections.some((s) => (s.kind === 'kpi' ? s.items?.length : s.rows?.length));
        if (!hasData) { toast.info('Nothing to export for the current view.'); return; }
        if (kind === 'xlsx') exportReportXlsx(filename, sections);
        else exportReportPdf(filename, { title, subtitle, sections, orientation });
        return;
      }

      const rows = await getRows();
      if (!rows || rows.length === 0) { toast.info('Nothing to export for the current view.'); return; }
      // Dynamic "Totaling & Summary" ledger over the same filtered dataset.
      const summary = getSummary ? await getSummary(rows) : null;
      if (kind === 'xlsx') exportToXlsx(filename, [{ name: (title || 'Report').slice(0, 31), columns, rows, summary }]);
      else exportToPdf(filename, { title, subtitle, columns, rows, orientation, summary });
    } catch (e) {
      toast.error('Could not generate the export. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const cls = `btn btn-outline btn-${size}`;
  return (
    <span style={{ display: 'inline-flex', gap: 6 }}>
      <button type="button" className={cls} disabled={busy} onClick={() => run('xlsx')} title="Export to Excel (.xlsx)">
        <FileSpreadsheet size={14} /> Excel
      </button>
      <button type="button" className={cls} disabled={busy} onClick={() => run('pdf')} title="Export to PDF">
        <FileText size={14} /> PDF
      </button>
    </span>
  );
}
